/**
 * Organisation access ("passage"): a Super Admin or the Owner of the platform organisation acting inside a
 * client organisation, with a chosen role, for a bounded time. Hand-copied from uzai-backend
 * (models/OrganizationAccess.ts and the /auth/access routes): resynchronise by hand.
 */

/** Roles a member of the platform team may act with inside a client organisation: Admin at most. */
export type AccessRole = 'ADMIN' | 'MANAGER' | 'OPERATOR' | 'CASE_OPERATOR' | 'COUNTRY_OPERATOR';

export const ACCESS_ROLES: AccessRole[] = ['ADMIN', 'MANAGER', 'OPERATOR', 'CASE_OPERATOR', 'COUNTRY_OPERATOR'];

/** Bounds checked by the backend: reason length, number of cases or territories in a scope. */
export const ACCESS_REASON_MAX_LENGTH = 200;
export const ACCESS_SCOPE_MAX_VALUES = 50;

/** The scope of a Case Operator (case ids) or of a Country Operator (territory codes). */
export interface AccessConstraint {
  name: 'CASE' | 'COUNTRY';
  values: string[];
}

/** The access a session acts under, as the backend sends it in `user.access`. */
export interface SessionAccess {
  id: string;
  organization: { id: string; name: string };
  role: AccessRole;
  expiresAt: string;
  reason?: string;
  constraint?: AccessConstraint;
}

/** Body of POST /auth/access. */
export interface AccessRequest {
  organizationId: string;
  role: AccessRole;
  reason?: string;
  constraint?: AccessConstraint;
}

/** A case of the client organisation, as GET /auth/access/organizations/:id/cases lists it. */
export interface AccessCase {
  _id: string;
  reference: string;
  identifier?: string;
  trademark: {
    name?: string;
    identifierNumber: string;
    countryOfDesignation: string;
  };
}

/** The constraint a role requires, if any. */
export function accessConstraintName(role: AccessRole): AccessConstraint['name'] | undefined {
  if (role === 'CASE_OPERATOR') return 'CASE';
  if (role === 'COUNTRY_OPERATOR') return 'COUNTRY';
  return undefined;
}
